import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Waves, Plus, Search, Filter, Play, Pencil, Trash2, Copy, FileText, Wind, Activity, Heart, Sparkles, Clock, ListPlus, Star, History, Trophy, LayoutGrid, List as ListIcon, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import TableBuilderModal from '../training/TableBuilderModal';
import TableExecutionView from '../training/TableExecutionView';
import ImportApneaTablesDialog from '../training/ImportApneaTablesDialog';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  CO2: { label: 'CO2 (Hypercapnique)', color: 'bg-orange-500/20 text-orange-200 border-orange-400/40', icon: Wind },
  O2: { label: 'O2 (Hypoxique)', color: 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40', icon: Activity },
  PV: { label: 'PV (Poumons Vides)', color: 'bg-purple-500/20 text-purple-200 border-purple-400/40', icon: Heart },
  OTHER: { label: 'Autres', color: 'bg-blue-500/20 text-blue-200 border-blue-400/40', icon: Sparkles },
};

const DIFFICULTY_META = {
  FACILE: { label: 'Facile', color: 'bg-green-500/20 text-green-200 border-green-400/40', dots: 1 },
  MOYENNE: { label: 'Moyenne', color: 'bg-yellow-500/20 text-yellow-200 border-yellow-400/40', dots: 2 },
  DIFFICILE: { label: 'Difficile', color: 'bg-orange-500/20 text-orange-200 border-orange-400/40', dots: 3 },
  TRES_DIFFICILE: { label: 'Très difficile', color: 'bg-red-500/20 text-red-200 border-red-400/40', dots: 4 },
  CUSTOM: { label: 'Personnalisée', color: 'bg-blue-500/20 text-blue-200 border-blue-400/40', dots: 0 },
};

const formatMMSS = (seconds) => {
  if (!seconds && seconds !== 0) return '--:--';
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

/**
 * Group steps into rounds of 2 (one round = 1 cycle ventilation+apnea or apnea+ventilation).
 * Column order is derived from the FIRST step's phase: if first is apnea → col1=Apnée (Bloquer), col2=Ventilation (Respirer).
 * Last round may have only one step if steps.length is odd.
 */
export const buildRounds = (steps = []) => {
  const rounds = [];
  for (let i = 0; i < steps.length; i += 2) {
    rounds.push({
      number: rounds.length + 1,
      a: steps[i] || null,
      b: steps[i + 1] || null,
      a_idx: i,
      b_idx: i + 1,
    });
  }
  const firstPhase = steps[0]?.phase || 'ventilation';
  const headerA = firstPhase === 'apnea' ? { label: 'Apnée', short: 'Bloquer', icon: '🔴', isApnea: true } : { label: 'Ventilation', short: 'Respirer', icon: '🟢', isApnea: false };
  const headerB = firstPhase === 'apnea' ? { label: 'Ventilation', short: 'Respirer', icon: '🟢', isApnea: false } : { label: 'Apnée', short: 'Bloquer', icon: '🔴', isApnea: true };
  return { rounds, headerA, headerB };
};

const ApneaTablesTab = ({ t, language = 'fr' }) => {
  const [tables, setTables] = useState([]);
  const [freedivers, setFreedivers] = useState([]);
  const [voiceSettings, setVoiceSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('ALL');
  const [filterDifficulty, setFilterDifficulty] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyTemplates, setShowOnlyTemplates] = useState(false);
  const [showOnlyCustom, setShowOnlyCustom] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [viewingTable, setViewingTable] = useState(null);
  const [executionTable, setExecutionTable] = useState(null);
  const [pbMode, setPbMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/apnea-tables`);
      setTables(res.data || []);
    } catch (err) {
      console.error('Failed to fetch apnea tables:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
    // Fetch freedivers list (used for Save Session dialog)
    axios
      .get(`${API_BASE_URL}/api/freedivers`)
      .then((res) => setFreedivers(res.data?.freedivers || []))
      .catch(() => setFreedivers([]));
    // Fetch settings to get voice_settings
    axios
      .get(`${API_BASE_URL}/api/settings`)
      .then((res) => setVoiceSettings(res.data?.voice_settings || {}))
      .catch(() => setVoiceSettings({}));
  }, []);

  const filteredTables = useMemo(() => {
    return tables.filter((tbl) => {
      if (filterType !== 'ALL' && tbl.type !== filterType) return false;
      if (filterDifficulty !== 'ALL' && (tbl.difficulty || 'CUSTOM') !== filterDifficulty) return false;
      if (showOnlyTemplates && !tbl.is_template) return false;
      if (showOnlyCustom && tbl.is_template) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!tbl.name.toLowerCase().includes(q) && !(tbl.description || '').toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [tables, filterType, filterDifficulty, searchQuery, showOnlyTemplates, showOnlyCustom]);

  const stats = useMemo(() => {
    return {
      total: tables.length,
      CO2: tables.filter((t) => t.type === 'CO2').length,
      O2: tables.filter((t) => t.type === 'O2').length,
      PV: tables.filter((t) => t.type === 'PV').length,
      OTHER: tables.filter((t) => t.type === 'OTHER').length,
      templates: tables.filter((t) => t.is_template).length,
      custom: tables.filter((t) => !t.is_template).length,
    };
  }, [tables]);

  const handleSave = async (payload, tableId) => {
    try {
      if (tableId) {
        await axios.put(`${API_BASE_URL}/api/apnea-tables/${tableId}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/api/apnea-tables`, payload);
      }
      await fetchTables();
      setShowBuilder(false);
      setEditingTable(null);
    } catch (err) {
      console.error('Failed to save table:', err);
      alert(`Erreur lors de l'enregistrement : ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDelete = async (tableId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/apnea-tables/${tableId}`);
      await fetchTables();
      setConfirmDelete(null);
    } catch (err) {
      console.error('Failed to delete table:', err);
    }
  };

  const handleDuplicate = async (table) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/apnea-tables/${table.id}/duplicate`,
        null,
        { params: { new_name: `${table.name} (copie)` } }
      );
      await fetchTables();
      setEditingTable(res.data);
      setShowBuilder(true);
    } catch (err) {
      console.error('Failed to duplicate table:', err);
    }
  };

  const handleExportPDF = (table) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(20, 60, 120);
    doc.text(table.name, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Type: ${TYPE_META[table.type]?.label || table.type}`, 14, 28);
    if (table.description) {
      doc.text(`${table.description}`, 14, 34);
    }
    doc.text(`Durée totale : ${formatMMSS(table.total_duration_seconds)} • ${table.apnea_count} apnées • Apnée la plus longue : ${formatMMSS(table.longest_apnea_seconds)}`, 14, 40);

    const { rounds, headerA, headerB } = buildRounds(table.steps || []);
    const rows = rounds.map((r) => [
      String(r.number),
      r.a ? formatMMSS(r.a.duration_seconds) : '—',
      r.b ? formatMMSS(r.b.duration_seconds) : '—',
    ]);

    autoTable(doc, {
      startY: 48,
      head: [['Round', `${headerA.label} (${headerA.short})`, `${headerB.label} (${headerB.short})`]],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: [20, 60, 120], textColor: 255, halign: 'center' },
      bodyStyles: { halign: 'center', fontSize: 11 },
      columnStyles: {
        0: { halign: 'center', cellWidth: 25, fontStyle: 'bold' },
        1: { halign: 'center', fillColor: headerA.isApnea ? [254, 226, 226] : [220, 252, 231] },
        2: { halign: 'center', fillColor: headerB.isApnea ? [254, 226, 226] : [220, 252, 231] },
      },
    });

    doc.save(`${table.name.replace(/[^a-z0-9]/gi, '_')}.pdf`);
  };

  const handleStartExecution = (table, isPb = false) => {
    setExecutionTable(table);
    setPbMode(isPb);
  };

  if (executionTable) {
    return (
      <TableExecutionView
        table={executionTable}
        pbMode={pbMode}
        freedivers={freedivers}
        voiceSettings={voiceSettings}
        onExit={() => {
          setExecutionTable(null);
          setPbMode(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="apnea-tables-tab">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-blue-100 flex items-center gap-3" data-testid="apnea-tables-header">
            <Waves className="text-cyan-400" size={32} />
            Tables d'Apnée
          </h2>
          <p className="text-blue-300 mt-1">Entraînement statique — CO2, O2, PV et tables personnalisées</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setShowImport(true)}
            variant="outline"
            className="border-teal-500/50 text-teal-200 hover:bg-teal-800/40"
            data-testid="import-apnea-tables-btn"
          >
            <Upload className="mr-2" size={18} />
            Importer
          </Button>
          <Button
            onClick={() => setShowHistory(true)}
            variant="outline"
            className="border-blue-600/50 text-blue-200 hover:bg-blue-800/40"
            data-testid="show-history-btn"
          >
            <History className="mr-2" size={18} />
            Historique
          </Button>
          <Button
            onClick={() => {
              setEditingTable(null);
              setShowBuilder(true);
            }}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="new-apnea-table-btn"
          >
            <Plus className="mr-2" size={18} />
            Nouvelle table
          </Button>
          <Button
            onClick={() =>
              handleStartExecution(
                {
                  id: 'pb-mode',
                  name: 'Personal Best',
                  type: 'OTHER',
                  description: 'Mode PB — apnée maximale',
                  steps: [
                    { phase: 'ventilation', duration_seconds: 120 },
                    { phase: 'apnea', duration_seconds: 99999 },
                  ],
                },
                true
              )
            }
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="pb-mode-btn"
          >
            <Star className="mr-2" size={18} />
            Mode PB
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="apnea-tables-stats">
        <Card className="bg-blue-900/40 border-blue-700/40 p-4">
          <div className="text-blue-300 text-xs uppercase tracking-wider">Total</div>
          <div className="text-3xl font-bold text-cyan-300 mt-1">{stats.total}</div>
        </Card>
        <Card className="bg-orange-900/30 border-orange-700/40 p-4">
          <div className="text-orange-300 text-xs uppercase tracking-wider">CO2</div>
          <div className="text-3xl font-bold text-orange-200 mt-1">{stats.CO2}</div>
        </Card>
        <Card className="bg-cyan-900/30 border-cyan-700/40 p-4">
          <div className="text-cyan-300 text-xs uppercase tracking-wider">O2</div>
          <div className="text-3xl font-bold text-cyan-200 mt-1">{stats.O2}</div>
        </Card>
        <Card className="bg-purple-900/30 border-purple-700/40 p-4">
          <div className="text-purple-300 text-xs uppercase tracking-wider">PV</div>
          <div className="text-3xl font-bold text-purple-200 mt-1">{stats.PV}</div>
        </Card>
        <Card className="bg-blue-900/30 border-blue-700/40 p-4">
          <div className="text-blue-300 text-xs uppercase tracking-wider">Autres</div>
          <div className="text-3xl font-bold text-blue-200 mt-1">{stats.OTHER}</div>
        </Card>
        <Card className="bg-teal-900/30 border-teal-700/40 p-4">
          <div className="text-teal-300 text-xs uppercase tracking-wider">Mes tables</div>
          <div className="text-3xl font-bold text-teal-200 mt-1">{stats.custom}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-blue-900/30 border-blue-700/30 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={16} />
            <Input
              placeholder="Rechercher une table..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-blue-950/50 border-blue-700/50 text-blue-100"
              data-testid="apnea-table-search"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px] bg-blue-950/50 border-blue-700/50 text-blue-100" data-testid="apnea-table-type-filter">
              <Filter size={16} className="mr-2 text-blue-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les types</SelectItem>
              <SelectItem value="CO2">CO2 (Hypercapnique)</SelectItem>
              <SelectItem value="O2">O2 (Hypoxique)</SelectItem>
              <SelectItem value="PV">PV (Poumons Vides)</SelectItem>
              <SelectItem value="OTHER">Autres</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
            <SelectTrigger className="w-[180px] bg-blue-950/50 border-blue-700/50 text-blue-100" data-testid="apnea-table-difficulty-filter">
              <Star size={16} className="mr-2 text-blue-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toutes difficultés</SelectItem>
              <SelectItem value="FACILE">Facile</SelectItem>
              <SelectItem value="MOYENNE">Moyenne</SelectItem>
              <SelectItem value="DIFFICILE">Difficile</SelectItem>
              <SelectItem value="TRES_DIFFICILE">Très difficile</SelectItem>
              <SelectItem value="CUSTOM">Personnalisée</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={showOnlyTemplates ? 'default' : 'outline'}
              onClick={() => {
                setShowOnlyTemplates(!showOnlyTemplates);
                setShowOnlyCustom(false);
              }}
              className={showOnlyTemplates ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-blue-600/50 text-blue-200'}
              data-testid="filter-templates-btn"
            >
              Templates ({stats.templates})
            </Button>
            <Button
              size="sm"
              variant={showOnlyCustom ? 'default' : 'outline'}
              onClick={() => {
                setShowOnlyCustom(!showOnlyCustom);
                setShowOnlyTemplates(false);
              }}
              className={showOnlyCustom ? 'bg-teal-600 hover:bg-teal-700' : 'border-blue-600/50 text-blue-200'}
              data-testid="filter-custom-btn"
            >
              Mes tables ({stats.custom})
            </Button>
          </div>
          {/* View toggle */}
          <div className="flex border border-blue-700/50 rounded-lg overflow-hidden ml-auto" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 transition-colors ${viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'bg-blue-950/50 text-blue-300 hover:bg-blue-800/50'}`}
              title="Vue cartes"
              data-testid="view-grid-btn"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 transition-colors ${viewMode === 'list' ? 'bg-cyan-600 text-white' : 'bg-blue-950/50 text-blue-300 hover:bg-blue-800/50'}`}
              title="Vue liste"
              data-testid="view-list-btn"
            >
              <ListIcon size={16} />
            </button>
          </div>
        </div>
      </Card>

      {/* Tables grid */}
      {loading ? (
        <div className="text-center py-12 text-blue-300">Chargement...</div>
      ) : filteredTables.length === 0 ? (
        <Card className="bg-blue-900/30 border-blue-700/30 p-12 text-center">
          <Waves className="mx-auto text-blue-500 mb-3" size={48} />
          <p className="text-blue-300">Aucune table trouvée. Créez votre première table !</p>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="bg-blue-900/30 border-blue-700/30 p-0 overflow-hidden" data-testid="apnea-tables-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-blue-900/50 text-blue-300 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Difficulté</th>
                  <th className="text-right px-4 py-3">Durée</th>
                  <th className="text-right px-4 py-3">Apnées</th>
                  <th className="text-right px-4 py-3">Max apnée</th>
                  <th className="text-center px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTables.map((table) => {
                  const meta = TYPE_META[table.type] || TYPE_META.OTHER;
                  const diffMeta = (table.difficulty && DIFFICULTY_META[table.difficulty]) || DIFFICULTY_META.CUSTOM;
                  const Icon = meta.icon;
                  return (
                    <tr
                      key={table.id}
                      className="border-t border-blue-800/40 hover:bg-blue-800/30 transition-colors"
                      onDoubleClick={() => setViewingTable(table)}
                      data-testid={`apnea-table-row-${table.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-blue-100 flex items-center gap-2">
                          {table.name}
                          {table.is_template && (
                            <Badge className="bg-yellow-500/20 text-yellow-200 border-yellow-500/40 text-[9px]">T</Badge>
                          )}
                        </div>
                        {table.description && <div className="text-blue-400 text-xs line-clamp-1">{table.description}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${meta.color} text-xs flex items-center gap-1 w-fit`}>
                          <Icon size={11} /> {table.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${diffMeta.color} text-xs w-fit`}>{diffMeta.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-cyan-300">
                        {formatMMSS(table.total_duration_seconds)}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-200">{table.apnea_count}</td>
                      <td className="px-4 py-3 text-right font-mono text-purple-300">
                        {formatMMSS(table.longest_apnea_seconds)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            onClick={() => handleStartExecution(table, false)}
                            className="bg-green-600 hover:bg-green-700 h-8 px-3"
                            data-testid={`list-start-btn-${table.id}`}
                          >
                            <Play size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingTable(table)}
                            className="border-blue-600/50 text-blue-200 h-8 w-8 p-0"
                            title="Voir"
                          >
                            <FileText size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingTable(table);
                              setShowBuilder(true);
                            }}
                            className="border-blue-600/50 text-blue-200 h-8 w-8 p-0"
                            title="Modifier"
                          >
                            <Pencil size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDuplicate(table)}
                            className="border-blue-600/50 text-blue-200 h-8 w-8 p-0"
                            title="Dupliquer"
                          >
                            <Copy size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleExportPDF(table)}
                            className="border-blue-600/50 text-blue-200 h-8 w-8 p-0"
                            title="Export PDF"
                          >
                            <FileText size={12} />
                          </Button>
                          {!table.is_template && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDelete(table.id)}
                              className="border-red-600/50 text-red-300 h-8 w-8 p-0"
                              title="Supprimer"
                            >
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                        {confirmDelete === table.id && (
                          <div className="mt-1 flex gap-1 justify-center">
                            <Button size="sm" onClick={() => handleDelete(table.id)} className="h-6 px-2 bg-red-600 text-xs">OK</Button>
                            <Button size="sm" variant="outline" onClick={() => setConfirmDelete(null)} className="h-6 px-2 text-xs">×</Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="apnea-tables-grid">
          {filteredTables.map((table) => {
            const meta = TYPE_META[table.type] || TYPE_META.OTHER;
            const Icon = meta.icon;
            return (
              <Card
                key={table.id}
                className="bg-gradient-to-br from-blue-900/50 to-blue-950/60 border-blue-700/40 p-5 hover:scale-[1.02] hover:border-cyan-500/50 transition-all cursor-pointer group"
                onDoubleClick={() => setViewingTable(table)}
                data-testid={`apnea-table-card-${table.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-100 text-lg leading-tight">{table.name}</h3>
                    {table.description && (
                      <p className="text-blue-400 text-xs mt-1 line-clamp-2">{table.description}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0 ml-2">
                    <Badge className={`${meta.color} text-xs flex items-center gap-1`}>
                      <Icon size={12} />
                      {table.type}
                    </Badge>
                    {table.difficulty && DIFFICULTY_META[table.difficulty] && (
                      <Badge className={`${DIFFICULTY_META[table.difficulty].color} text-[10px]`} data-testid={`difficulty-badge-${table.id}`}>
                        {DIFFICULTY_META[table.difficulty].label}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 my-3 text-center">
                  <div className="bg-blue-950/50 rounded-lg p-2">
                    <Clock size={14} className="mx-auto text-blue-400 mb-1" />
                    <div className="text-cyan-300 font-bold text-sm">{formatMMSS(table.total_duration_seconds)}</div>
                    <div className="text-blue-500 text-[10px]">Durée totale</div>
                  </div>
                  <div className="bg-blue-950/50 rounded-lg p-2">
                    <ListPlus size={14} className="mx-auto text-blue-400 mb-1" />
                    <div className="text-cyan-300 font-bold text-sm">{table.apnea_count}</div>
                    <div className="text-blue-500 text-[10px]">Apnées</div>
                  </div>
                  <div className="bg-blue-950/50 rounded-lg p-2">
                    <Star size={14} className="mx-auto text-blue-400 mb-1" />
                    <div className="text-cyan-300 font-bold text-sm">{formatMMSS(table.longest_apnea_seconds)}</div>
                    <div className="text-blue-500 text-[10px]">Max apnée</div>
                  </div>
                </div>

                {table.is_template && (
                  <Badge className="bg-yellow-500/20 text-yellow-200 border-yellow-500/40 text-[10px] mb-2">
                    Template
                  </Badge>
                )}

                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => handleStartExecution(table, false)}
                    className="flex-1 bg-green-600 hover:bg-green-700 min-h-[40px]"
                    data-testid={`start-table-btn-${table.id}`}
                  >
                    <Play size={14} className="mr-1" />
                    Commencer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setViewingTable(table)}
                    className="border-blue-600/50 text-blue-200 hover:bg-blue-800/50"
                    title="Voir les détails"
                    data-testid={`view-table-btn-${table.id}`}
                  >
                    <FileText size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingTable(table);
                      setShowBuilder(true);
                    }}
                    className="border-blue-600/50 text-blue-200 hover:bg-blue-800/50"
                    title="Modifier"
                    data-testid={`edit-table-btn-${table.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDuplicate(table)}
                    className="border-blue-600/50 text-blue-200 hover:bg-blue-800/50"
                    title="Dupliquer"
                    data-testid={`duplicate-table-btn-${table.id}`}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExportPDF(table)}
                    className="border-blue-600/50 text-blue-200 hover:bg-blue-800/50"
                    title="Exporter PDF"
                    data-testid={`pdf-table-btn-${table.id}`}
                  >
                    <FileText size={14} />
                  </Button>
                  {!table.is_template && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmDelete(table.id)}
                      className="border-red-600/50 text-red-300 hover:bg-red-800/30"
                      title="Supprimer"
                      data-testid={`delete-table-btn-${table.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>

                {confirmDelete === table.id && (
                  <div className="mt-3 p-2 bg-red-900/30 border border-red-700/50 rounded-lg flex items-center justify-between gap-2">
                    <span className="text-red-200 text-xs">Confirmer la suppression ?</span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDelete(table.id)}
                        className="h-7 px-3 bg-red-600 hover:bg-red-700 text-xs"
                        data-testid={`confirm-delete-${table.id}`}
                      >
                        OK
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmDelete(null)}
                        className="h-7 px-3 text-xs"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Builder modal */}
      {showBuilder && (
        <TableBuilderModal
          open={showBuilder}
          editingTable={editingTable}
          onClose={() => {
            setShowBuilder(false);
            setEditingTable(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* View modal */}
      {viewingTable && (
        <ViewTableModal
          table={viewingTable}
          onClose={() => setViewingTable(null)}
          onStart={() => {
            setExecutionTable(viewingTable);
            setViewingTable(null);
          }}
          onExportPDF={handleExportPDF}
        />
      )}

      {/* History modal */}
      {showHistory && (
        <HistoryModal onClose={() => setShowHistory(false)} freedivers={freedivers} />
      )}

      {/* Import dialog (Excel + PDF AI) */}
      <ImportApneaTablesDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onComplete={fetchTables}
      />
    </div>
  );
};

// History modal — list of executed sessions with stats
const HistoryModal = ({ onClose, freedivers = [] }) => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterFreediver, setFilterFreediver] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = filterFreediver && filterFreediver !== 'all' ? { freediver_id: filterFreediver } : {};
        const [sRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/apnea-table-sessions`, { params }),
          axios.get(`${API_BASE_URL}/api/apnea-table-sessions/stats`, { params }),
        ]);
        setSessions(sRes.data || []);
        setStats(statsRes.data || null);
      } catch (e) {
        console.error('History load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterFreediver]);

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/apnea-table-sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      alert('Erreur de suppression');
    }
  };

  const fdName = (id) => {
    const fd = freedivers.find((f) => f.id === id);
    return fd ? `${fd.first_name} ${fd.last_name}` : '—';
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="history-modal"
    >
      <Card
        className="bg-blue-950/95 border-blue-700/50 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-blue-800/50 flex items-center justify-between">
          <h3 className="text-2xl font-bold text-blue-100 flex items-center gap-2">
            <History size={24} />
            Historique d'exécution
          </h3>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-blue-300">
            ×
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 border-b border-blue-800/50">
            <Card className="bg-blue-900/40 border-blue-700/40 p-3 text-center">
              <div className="text-blue-300 text-xs uppercase">Sessions</div>
              <div className="text-2xl font-bold text-cyan-300">{stats.total_sessions}</div>
            </Card>
            <Card className="bg-blue-900/40 border-blue-700/40 p-3 text-center">
              <div className="text-blue-300 text-xs uppercase">Complétées</div>
              <div className="text-2xl font-bold text-green-300">{stats.completed_sessions}</div>
            </Card>
            <Card className="bg-blue-900/40 border-blue-700/40 p-3 text-center">
              <div className="text-blue-300 text-xs uppercase">Temps total</div>
              <div className="text-2xl font-bold text-blue-200">{formatMMSS(Math.floor(stats.total_duration_seconds))}</div>
            </Card>
            <Card className="bg-purple-900/40 border-purple-700/40 p-3 text-center">
              <div className="text-purple-300 text-xs uppercase flex items-center justify-center gap-1">
                <Trophy size={12} /> PB Apnée
              </div>
              <div className="text-2xl font-bold text-purple-200">{formatMMSS(Math.floor(stats.pb_apnea_seconds))}</div>
            </Card>
          </div>
        )}

        {/* Filter */}
        {freedivers.length > 0 && (
          <div className="p-4 border-b border-blue-800/50">
            <Select value={filterFreediver} onValueChange={setFilterFreediver}>
              <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100 max-w-xs" data-testid="history-freediver-filter">
                <SelectValue placeholder="Tous les apnéistes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les apnéistes</SelectItem>
                {freedivers.map((fd) => (
                  <SelectItem key={fd.id} value={fd.id}>
                    {fd.first_name} {fd.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Sessions list */}
        <div className="overflow-y-auto p-4 flex-1">
          {loading ? (
            <div className="text-center py-8 text-blue-300">Chargement...</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-blue-400">
              <History className="mx-auto mb-2" size={32} />
              <p>Aucune session enregistrée pour le moment.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="sessions-list">
              {sessions.map((s) => (
                <Card key={s.id} className="bg-blue-900/30 border-blue-800/40 p-3" data-testid={`session-row-${s.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-blue-100 font-medium">{s.table_name || 'Table inconnue'}</span>
                        {s.is_pb_mode && (
                          <Badge className="bg-purple-500/30 text-purple-200 border-purple-400/40 text-[10px]">
                            PB Mode
                          </Badge>
                        )}
                        <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/40 text-[10px]">
                          {s.table_type}
                        </Badge>
                        {s.performance_id && (
                          <Badge className="bg-yellow-500/30 text-yellow-200 border-yellow-400/40 text-[10px] flex items-center gap-1">
                            <Trophy size={10} /> Carrière
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-blue-400 mt-1">
                        {s.started_at ? new Date(s.started_at).toLocaleString('fr-FR') : '—'} •{' '}
                        {s.freediver_id ? fdName(s.freediver_id) : 'Sans apnéiste'}
                      </div>
                      {s.notes && <div className="text-xs text-blue-300 mt-1 italic">"{s.notes}"</div>}
                    </div>
                    <div className="flex gap-3 text-center">
                      <div>
                        <div className="text-cyan-300 font-mono font-bold">{formatMMSS(Math.floor(s.duration_seconds || 0))}</div>
                        <div className="text-[10px] text-blue-400">Durée</div>
                      </div>
                      {s.max_apnea_seconds > 0 && (
                        <div>
                          <div className="text-purple-300 font-mono font-bold">{formatMMSS(Math.floor(s.max_apnea_seconds))}</div>
                          <div className="text-[10px] text-blue-400">Max apnée</div>
                        </div>
                      )}
                      <div>
                        <div className="text-red-300 font-bold">{s.contractions_count || 0}</div>
                        <div className="text-[10px] text-blue-400">Contractions</div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteSession(s.id)}
                      className="text-red-300 hover:bg-red-900/30 h-8 w-8 p-0"
                      data-testid={`delete-session-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// Inline view modal — read-only details + start button
const ViewTableModal = ({ table, onClose, onStart, onExportPDF }) => {
  const meta = TYPE_META[table.type] || TYPE_META.OTHER;
  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="view-table-modal"
    >
      <Card
        className="bg-blue-950/95 border-blue-700/50 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-blue-800/50 flex items-start justify-between">
          <div>
            <Badge className={`${meta.color} mb-2`}>{table.type}</Badge>
            <h3 className="text-2xl font-bold text-blue-100">{table.name}</h3>
            {table.description && <p className="text-blue-300 text-sm mt-1">{table.description}</p>}
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-blue-300 -mt-2">
            ×
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3 p-5 border-b border-blue-800/50">
          <div className="text-center">
            <div className="text-xs text-blue-400 uppercase">Durée totale</div>
            <div className="text-2xl font-bold text-cyan-300">{formatMMSS(table.total_duration_seconds)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-blue-400 uppercase">Apnées</div>
            <div className="text-2xl font-bold text-cyan-300">{table.apnea_count}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-blue-400 uppercase">Max apnée</div>
            <div className="text-2xl font-bold text-cyan-300">{formatMMSS(table.longest_apnea_seconds)}</div>
          </div>
        </div>
        <div className="overflow-y-auto p-5">
          {(() => {
            const { rounds, headerA, headerB } = buildRounds(table.steps || []);
            return (
              <table className="w-full text-sm" data-testid="view-rounds-table">
                <thead className="text-blue-300 text-xs uppercase border-b border-blue-800/50">
                  <tr>
                    <th className="text-left py-2 px-2 w-12">#</th>
                    <th className="text-center py-2 px-2">
                      <div className="flex flex-col items-center">
                        <span className="text-base">{headerA.icon} {headerA.label}</span>
                        <span className="text-[10px] opacity-70 normal-case">({headerA.short})</span>
                      </div>
                    </th>
                    <th className="text-center py-2 px-2">
                      <div className="flex flex-col items-center">
                        <span className="text-base">{headerB.icon} {headerB.label}</span>
                        <span className="text-[10px] opacity-70 normal-case">({headerB.short})</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rounds.map((r) => (
                    <tr
                      key={r.number}
                      className="border-b border-blue-900/30 hover:bg-blue-900/20"
                      data-testid={`view-round-${r.number}`}
                    >
                      <td className="py-3 px-2 text-blue-400 font-mono">{r.number}</td>
                      <td className={`py-3 px-2 text-center font-mono text-lg ${headerA.isApnea ? 'text-red-300 bg-red-900/10' : 'text-green-300 bg-green-900/10'}`}>
                        {r.a ? formatMMSS(r.a.duration_seconds) : '—'}
                      </td>
                      <td className={`py-3 px-2 text-center font-mono text-lg ${headerB.isApnea ? 'text-red-300 bg-red-900/10' : 'text-green-300 bg-green-900/10'}`}>
                        {r.b ? formatMMSS(r.b.duration_seconds) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
        <div className="p-5 border-t border-blue-800/50 flex gap-3">
          <Button onClick={onStart} className="flex-1 bg-green-600 hover:bg-green-700 min-h-[48px]" data-testid="view-modal-start-btn">
            <Play className="mr-2" size={18} />
            Commencer
          </Button>
          <Button onClick={() => onExportPDF(table)} variant="outline" className="border-blue-600 text-blue-200">
            <FileText className="mr-2" size={18} />
            PDF
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ApneaTablesTab;
